// src/components/dashboard/ProfileSection.js
import React from 'react';
import { motion } from 'framer-motion';
import { 
  User, Mail, Phone, MapPin, Calendar, Shield, 
  Edit2, Lock, Bell, CreditCard, ShoppingBag, Star 
} from 'lucide-react';

function ProfileSection({ user, setCurrentPage, isMobile }) {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-600 flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            My Profile
          </h2>
          <p className="text-gray-400">Manage your account and preferences</p>
        </div>
      </div>

      <div className="relative">
        {/* Glow Effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-3xl blur-xl"></div>
        
        {/* Profile Container */}
        <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-purple-500/20 p-8">
          {/* Profile Header */}
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8 mb-12">
            <div className="relative">
              {user?.imageUrl ? (
                <div className="relative">
                  <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-gray-900">
                    <img 
                      src={user.imageUrl} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl blur-md -z-10"></div>
                </div>
              ) : (
                <div className="relative">
                  <div className="w-32 h-32 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-4xl font-bold text-white">
                    {user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase()}
                  </div>
                  <div className="absolute -inset-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl blur-md -z-10"></div>
                </div>
              )}
            </div>
            
            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-3xl font-bold text-white">{user?.name || 'User'}</h3>
                  <p className="text-gray-400 mt-2">{user?.email}</p>
                  <div className="flex items-center gap-3 mt-4">
                    <span className="px-3 py-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 rounded-full text-sm font-semibold">
                      {user?.role === 'staff' ? 'Staff Member' : 'Premium Customer'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${user?.isVerified ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-400' : 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-yellow-400'}`}>
                      {user?.isVerified ? '✓ Verified' : 'Pending Verification'}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage('profile')}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center gap-2"
                  >
                    <Edit2 className="w-5 h-5" />
                    <span>Edit Profile</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage('change-password')}
                    className="px-6 py-3 bg-gray-800 border border-gray-700 text-gray-300 rounded-xl font-bold hover:bg-gray-700 transition-colors flex items-center gap-2"
                  >
                    <Lock className="w-5 h-5" />
                    <span>Security</span>
                  </motion.button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800/30 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Total Orders</div>
                  <div className="text-2xl font-bold text-white">24</div>
                </div>
                <div className="bg-gray-800/30 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Total Spent</div>
                  <div className="text-2xl font-bold text-emerald-400">$1,248</div>
                </div>
                <div className="bg-gray-800/30 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Member Since</div>
                  <div className="text-lg font-bold text-white">Jan 2024</div>
                </div>
                <div className="bg-gray-800/30 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Rating</div>
                  <div className="text-lg font-bold text-amber-400 flex items-center gap-1">
                    <Star className="w-4 h-4 fill-amber-400" />
                    4.8
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500/20 to-cyan-500/20">
                  <User className="w-5 h-5 text-blue-400" />
                </div>
                Personal Information
              </h4>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Email</div>
                    <div className="text-white">{user?.email || 'Not provided'}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Phone</div>
                    <div className="text-white">{user?.phone || 'Not provided'}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Address</div>
                    <div className="text-white">{user?.address || 'Not provided'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h4 className="text-xl font-bold text-white flex items-center gap-2">
                <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                Account Details
              </h4>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Joined</div>
                    <div className="text-white">January 15, 2024</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Last Order</div>
                    <div className="text-white">2 days ago</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Payment Methods</div>
                    <div className="text-white">2 cards saved</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-12 pt-8 border-t border-gray-800">
            <h4 className="text-xl font-bold text-white mb-6">Quick Actions</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                onClick={() => setCurrentPage('profile')}
                className="p-4 bg-gray-800/30 hover:bg-gray-800/50 rounded-xl border border-gray-700 text-white text-center transition-colors"
              >
                <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                  <Edit2 className="w-5 h-5 text-purple-400" />
                </div>
                Edit Profile
              </button>
              
              <button 
                onClick={() => setCurrentPage('change-password')}
                className="p-4 bg-gray-800/30 hover:bg-gray-800/50 rounded-xl border border-gray-700 text-white text-center transition-colors"
              >
                <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-r from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-blue-400" />
                </div>
                Change Password
              </button>
              
              <button 
                onClick={() => setCurrentPage('notifications')}
                className="p-4 bg-gray-800/30 hover:bg-gray-800/50 rounded-xl border border-gray-700 text-white text-center transition-colors"
              >
                <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-r from-emerald-500/20 to-green-500/20 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-emerald-400" />
                </div>
                Notifications
              </button>
              
              <button 
                onClick={() => setCurrentPage('orders')}
                className="p-4 bg-gray-800/30 hover:bg-gray-800/50 rounded-xl border border-gray-700 text-white text-center transition-colors"
              >
                <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-to-r from-amber-500/20 to-orange-500/20 flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5 text-amber-400" />
                </div>
                My Orders
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfileSection;